import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_BRANDING,
  imageFormats,
  variantsByFormat,
  type Branding,
  type BrandingAssetKind,
  type BrandingImage,
  type BrandingImageFormat,
  type BrandingImageQuality,
  type BrandingResponseDto,
  type BrandingUpdateInput,
} from '@fms/contracts';
import { useApiClient } from './provider';

export const BRANDING_QUERY_KEY = ['branding'] as const;

/**
 * Portal branding. Both apps read this before anything else, so it is tuned for
 * the cold-load path: one anonymous GET, a long stale window (the document
 * changes when an operator publishes, not while someone is signing in), and no
 * refetch on focus. The shipped defaults stand in while it is in flight and if
 * it fails outright — a login screen must never render unbranded because the
 * network hiccuped.
 */
export function useBranding() {
  const client = useApiClient();
  return useQuery({
    queryKey: BRANDING_QUERY_KEY,
    queryFn: async () => (await client.axios.get<BrandingResponseDto>('/branding')).data,
    staleTime: 10 * 60_000,
    gcTime: 60 * 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
    // `initialData`, not `placeholderData`: a placeholder only fills the PENDING
    // state, so the moment the GET failed outright `data` reverted to undefined
    // and every `brandingData!.branding` in the portals threw — a dead API
    // white-screened the login page with "Cannot read properties of undefined".
    // Initial data is written to the cache instead, so it survives the error and
    // the defaults really do stand in. `initialDataUpdatedAt: 0` backdates it so
    // the long `staleTime` cannot mistake the defaults for a fresh fetch and
    // skip the request.
    initialData: {
      branding: DEFAULT_BRANDING,
      assetBaseUrl: `${client.axios.defaults.baseURL ?? ''}/branding/img`,
      etag: 'default',
    } satisfies BrandingResponseDto,
    initialDataUpdatedAt: 0,
  });
}

/**
 * Adopt a mutation's own response as the new cache entry.
 *
 * Every write returns the full published document, so there is nothing to go
 * back to the server for — and doing so was the bug: an `invalidateQueries`
 * alone re-issued `GET /branding`, which the browser answered from its own
 * cache, so a freshly uploaded logo appeared to vanish until a hard refresh.
 * Writing the result straight in makes the screen correct on the same tick; the
 * follow-up invalidate is only there to pick up a concurrent operator's edit.
 */
function useBrandingWriter() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return (branding: Branding, etag: string) => {
    queryClient.setQueryData(BRANDING_QUERY_KEY, (previous: BrandingResponseDto | undefined) => ({
      branding,
      // The GET is what normally supplies this; fall back to the same derivation
      // the query placeholder uses so a write that lands first still has it.
      assetBaseUrl: previous?.assetBaseUrl ?? `${client.axios.defaults.baseURL ?? ''}/branding/img`,
      etag,
    }));
    void queryClient.invalidateQueries({ queryKey: BRANDING_QUERY_KEY });
  };
}

export function useUpdateBranding() {
  const client = useApiClient();
  const write = useBrandingWriter();
  return useMutation({
    mutationFn: async (patch: BrandingUpdateInput) =>
      (await client.axios.put<{ branding: Branding; etag: string }>('/branding', patch)).data,
    onSuccess: (data) => write(data.branding, data.etag),
  });
}

export interface UploadBrandingAssetInput {
  kind: BrandingAssetKind;
  file: File;
  /** Delivery format. 'avif' additionally emits a WebP fallback set. */
  format?: BrandingImageFormat;
  /** Encoding preset for this upload's renditions; the server defaults to 'balanced'. */
  quality?: BrandingImageQuality;
  alt?: string;
  onProgress?: (percent: number) => void;
}

/**
 * Upload one branding image. The bytes go up untouched — unlike a citizen's
 * camera photo, this is a deliberate act by an operator on a desk connection,
 * and the server's sharp pass is the only place the rendition set should be
 * decided. Publishing happens server-side in the same request, so a successful
 * upload is immediately live.
 */
export function useUploadBrandingAsset() {
  const client = useApiClient();
  const write = useBrandingWriter();
  return useMutation({
    mutationFn: async ({
      kind,
      file,
      format,
      quality,
      alt,
      onProgress,
    }: UploadBrandingAssetInput) => {
      const body = new FormData();
      body.append('file', file, file.name);
      const params = new URLSearchParams({ kind });
      if (format) params.set('format', format);
      if (quality) params.set('quality', quality);
      if (alt) params.set('alt', alt);
      const response = await client.axios.post<{
        kind: BrandingAssetKind;
        image: BrandingImage;
        branding: Branding;
        etag: string;
      }>(`/branding/assets?${params.toString()}`, body, {
        onUploadProgress: (event) => {
          if (onProgress && event.total) {
            onProgress(Math.round((event.loaded / event.total) * 100));
          }
        },
      });
      return response.data;
    },
    onSuccess: (data) => write(data.branding, data.etag),
  });
}

export function useClearBrandingAsset() {
  const client = useApiClient();
  const write = useBrandingWriter();
  return useMutation({
    mutationFn: async (kind: BrandingAssetKind) =>
      (await client.axios.delete<{ branding: Branding; etag: string }>(`/branding/assets/${kind}`))
        .data,
    onSuccess: (data) => write(data.branding, data.etag),
  });
}

/** Absolute URL of one rendition. */
export function brandingAssetUrl(assetBaseUrl: string, assetId: string): string {
  return `${assetBaseUrl.replace(/\/$/, '')}/${assetId}`;
}

export interface BrandingSourceSet {
  format: BrandingImageFormat;
  type: string;
  srcSet: string;
}

/**
 * Build the `<source>` list for a `<picture>`: one entry per stored format,
 * best-compressing first, each with a width-descriptor srcset so the browser
 * picks the rendition that matches the device's viewport and DPR. Returns the
 * widest WebP (or whatever is widest overall) as `fallbackSrc` for the `<img>`.
 */
export function brandingSources(image: BrandingImage, assetBaseUrl: string) {
  const sources: BrandingSourceSet[] = [];
  for (const format of imageFormats(image)) {
    const variants = variantsByFormat(image, format);
    if (variants.length === 0) continue;
    sources.push({
      format,
      type: `image/${format}`,
      srcSet: variants
        .map((v) => `${brandingAssetUrl(assetBaseUrl, v.assetId)} ${v.width}w`)
        .join(', '),
    });
  }
  // The plain <img> must name a universally-decodable format; WebP is the one
  // the pipeline always emits, so prefer it even when AVIF renditions exist.
  const webp = variantsByFormat(image, 'webp');
  const fallback = (
    webp.length > 0 ? webp : [...image.variants].sort((a, b) => a.width - b.width)
  ).slice(-1)[0];
  return {
    sources,
    fallbackSrc: fallback ? brandingAssetUrl(assetBaseUrl, fallback.assetId) : '',
    width: image.width,
    height: image.height,
  };
}
